import { useEffect, useRef, useState } from 'react';

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return { ref, inView };
}

const plans = [
  {
    name: 'Štart',
    badge: null,
    price: '590',
    priceNote: 'jednorazovo',
    description: 'Ideálny pre živnostníkov a malé firmy, ktoré potrebujú profesionálnu online prítomnosť.',
    features: [
      'Do 5 podstránok',
      'Responzívny dizajn (mobil + PC)',
      'Kontaktný formulár',
      'SEO základy',
      'SSL certifikát',
      'Odovzdanie do 10 dní',
      'Správa obsahu (CMS)',
      '30 dní podpora po spustení',
    ],
    notIncluded: ['Blog / novinky', 'Pokročilé animácie', 'Viacjazyčná verzia'],
    cta: 'Začať s balíkom Štart',
    color: 'border-zinc-200',
    ctaStyle: 'bg-zinc-900 hover:bg-zinc-800 text-white',
  },
  {
    name: 'Biznis',
    badge: '⭐ Najobľúbenejší',
    price: '990',
    priceNote: 'jednorazovo',
    description: 'Pre firmy, ktoré chcú web, čo aktívne predáva a buduje dôveru zákazníkov.',
    features: [
      'Do 10 podstránok',
      'Responzívny dizajn (mobil + PC)',
      'Kontaktný formulár + rezervácie',
      'SEO optimalizácia',
      'SSL certifikát',
      'Odovzdanie do 12 dní',
      'Blog / novinky sekcia',
      'Pokročilé animácie',
      'Google Analytics nastavenie',
      '60 dní podpora po spustení',
    ],
    notIncluded: ['Viacjazyčná verzia'],
    cta: 'Začať s balíkom Biznis',
    color: 'border-orange-400',
    ctaStyle: 'bg-orange-500 hover:bg-orange-400 text-white',
    featured: true,
  },
  {
    name: 'Pro',
    badge: 'Na mieru bez kompromisov',
    price: 'Od 1 890',
    priceNote: 'podľa rozsahu',
    description: 'Komplexné riešenie pre firmy s vyššími nárokmi — viacjazyčnosť, integrácie, vlastné funkcie.',
    features: [
      'Neobmedzený počet podstránok',
      'Responzívny dizajn (mobil + PC)',
      'Všetky typy formulárov',
      'Pokročilé SEO + technický audit',
      'SSL certifikát',
      'Viacjazyčná verzia',
      'Vlastné integrácie (CRM, API)',
      'Pokročilé animácie a interakcie',
      'Google Analytics + Search Console',
      '90 dní podpora po spustení',
    ],
    notIncluded: [],
    cta: 'Nezáväzná konzultácia',
    color: 'border-zinc-200',
    ctaStyle: 'bg-zinc-900 hover:bg-zinc-800 text-white',
  },
];

interface TWPricingProps {
  onCtaClick: () => void;
}

export default function TWPricing({ onCtaClick }: TWPricingProps) {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <div ref={ref}>
          <div
            className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-price-tag-3-line text-orange-500 text-sm" />
              <span className="text-orange-600 text-sm font-semibold">Transparentný cenník</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-zinc-900 mb-4 leading-tight">
              Vyberte si balík, ktorý sedí vašej firme
            </h2>
            <p className="text-zinc-500 text-base leading-relaxed">
              Žiadne skryté poplatky. Cena zahŕňa dizajn, vývoj, texty aj spustenie.
              Doménu a hosting vieme zabezpečiť za príplatok.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {plans.map((plan, i) => (
              <div
                key={plan.name}
                className={`relative flex flex-col rounded-2xl border-2 ${plan.color} ${plan.featured ? 'shadow-xl shadow-orange-100' : ''} transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${i * 120}ms` }}
              >
                {plan.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-orange-500 text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap">
                    {plan.badge}
                  </div>
                )}
                {!plan.featured && plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-zinc-800 text-zinc-300 text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap">
                    {plan.badge}
                  </div>
                )}

                <div className={`p-7 flex-1 flex flex-col ${plan.featured ? 'bg-orange-50/30' : 'bg-white'} rounded-2xl`}>
                  <div className="mb-6">
                    <h3 className="text-zinc-900 font-black text-xl mb-2">{plan.name}</h3>
                    <p className="text-zinc-500 text-sm leading-relaxed">{plan.description}</p>
                  </div>

                  <div className="mb-7">
                    <div className="flex items-end gap-2">
                      <span className="text-4xl font-black text-zinc-900">{plan.price} €</span>
                    </div>
                    <span className="text-zinc-400 text-sm">{plan.priceNote}</span>
                  </div>

                  <div className="flex-1 space-y-3 mb-7">
                    {plan.features.map((feature) => (
                      <div key={feature} className="flex items-start gap-2.5">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-check-line text-emerald-500 text-sm font-bold" />
                        </div>
                        <span className="text-zinc-700 text-sm">{feature}</span>
                      </div>
                    ))}
                    {plan.notIncluded.map((feature) => (
                      <div key={feature} className="flex items-start gap-2.5 opacity-40">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <i className="ri-close-line text-zinc-400 text-sm" />
                        </div>
                        <span className="text-zinc-500 text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={onCtaClick}
                    className={`w-full ${plan.ctaStyle} font-bold py-3.5 rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap text-sm`}
                  >
                    {plan.cta}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div
            className={`text-center transition-all duration-700 delay-400 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
          >
            <p className="text-zinc-400 text-sm">
              Nie ste si istí, ktorý balík je pre vás?{' '}
              <button
                onClick={onCtaClick}
                className="text-orange-500 hover:text-orange-600 font-semibold cursor-pointer underline underline-offset-2"
              >
                Porozprávajme sa zadarmo
              </button>{' '}
              — spoločne nájdeme to správne riešenie.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
